import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNode, useEditor } from '@craftjs/core';
import { Resizable } from 're-resizable';
import { debounce } from 'debounce';
import { makeStyles } from '@material-ui/core/styles';
import { usePropagateDimensions } from '../../hooks/usePropagateDimensions';
import { isPercentage, pxToPercent, percentToPx, getElementDimensions } from '../../utils/numToMeasurement';

const useStyles = makeStyles({
    indicators: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        '& span': {
            position: 'absolute',
            width: '10px',
            height: '10px',
            background: '#fff',
            borderRadius: '100%',
            display: 'block',
            boxShadow: '0px 0px 12px -1px rgba(0, 0, 0, 0.25)',
            zIndex: 8888,
            pointerEvents: 'none',
            border: '2px solid #36a9e0',
        },
        '& span:nth-child(1)': props => ({
            ...(!props.bound
                ? { left: '-5px', top: '-5px' }
                : props.bound === 'row'
                ? { left: '50%', top: '-5px', transform: 'translateX(-50%)' }
                : { top: '50%', left: '-5px', transform: 'translateY(-50%)' })
        }),
        '& span:nth-child(2)': props => ({
            right: '-5px',
            top: '-5px',
            display: props.bound ? 'none' : 'block',
        }),
        '& span:nth-child(3)': props => ({
            ...(!props.bound
                ? { left: '-5px', bottom: '-5px' }
                : props.bound === 'row'
                ? { left: '50%', bottom: '-5px', transform: 'translateX(-50%)' }
                : { bottom: '50%', left: '-5px', transform: 'translateY(-50%)' })
        }),
        '& span:nth-child(4)': props => ({
            bottom: '-5px',
            right: '-5px',
            display: props.bound ? 'none' : 'block',
        }),
    },
    resizable: props => ({
        display: 'flex',
        ...(props.isRootNode && { margin: 'auto' }),
    }),
});

export const Resizer = ({ propKey, children, aspectRatio, ...props }) => {
    const {
        id,
        actions: { setProp },
        connectors: { connect },
        fillSpace,
        nodeWidth,
        nodeHeight,
        parent,
        active,
        inNodeContext,
        updatedAt,
    } = useNode((node) => ({
        parent: node.data.parent,
        active: node.events.selected,
        nodeWidth: node.data.props[propKey.width],
        nodeHeight: node.data.props[propKey.height],
        fillSpace: node.data.props.fillSpace,
        updatedAt: node.data.props.updatedAt,
    }));

    const { isRootNode, parentDirection, actions, query } = useEditor((state, query) => ({
        parentDirection:
            parent &&
            state.nodes[parent] &&
            state.nodes[parent].data.props.flexDirection,
        isRootNode: query.node(id).isRoot(),
        actions: state.actions,
    }));
    
    const classes = useStyles({
        bound: fillSpace === 'yes' ? parentDirection : false,
        isRootNode,
    });

    const resizable = useRef(null);
    const isResizing = useRef(false);
    const editingDimensions = useRef(null);
    const currentDragSize = useRef({ width: nodeWidth, height: nodeHeight }); 
    const nodeDimensions = useRef({ width: nodeWidth, height: nodeHeight });
    nodeDimensions.current = { width: nodeWidth, height: nodeHeight };

    const [internalDimensions, setInternalDimensions] = useState({
        width: nodeWidth,
        height: nodeHeight,
    });

    const resizeSide = useRef(null); 

    const [minDimensions, setMinDimensions] = useState({ minWidth: 0, minHeight: 0 });
    
    const calculateMinDimensions = useCallback(() => {
        if (!resizable.current || !resizable.current.resizable) return;
        const dom = resizable.current.resizable;

        const originalWidth = dom.style.width;
        const originalHeight = dom.style.height;
        const originalMinWidth = dom.style.minWidth;
        const originalMinHeight = dom.style.minHeight;

        dom.style.width = 'auto';
        dom.style.height = 'auto';
        dom.style.minWidth = '0px';
        dom.style.minHeight = '0px';

        const bounds = dom.getBoundingClientRect();

        let contentMinHeight = bounds.height;
        let contentMinWidth = bounds.width;

        dom.style.width = originalWidth;
        dom.style.height = originalHeight;
        dom.style.minWidth = originalMinWidth; 
        dom.style.minHeight = originalMinHeight;
        
        const MIN_ABSOLUTO_PX = 5;
      
        const UMBRAL_MAXIMO_ACEPTABLE = 150; 

        if (contentMinHeight > UMBRAL_MAXIMO_ACEPTABLE) {
            contentMinHeight = MIN_ABSOLUTO_PX;
        }
        if (contentMinWidth > UMBRAL_MAXIMO_ACEPTABLE) {
            contentMinWidth = MIN_ABSOLUTO_PX;
        }
        const finalMinHeight = Math.max(contentMinHeight, MIN_ABSOLUTO_PX);
        const finalMinWidth = Math.max(contentMinWidth, MIN_ABSOLUTO_PX);

        setMinDimensions({
            minWidth: finalMinWidth,
            minHeight: finalMinHeight,
        });
    }, [id]); 
    
    const { propagateHeight, propagateWidth, MAX_RECIPE_WIDTH_PX } = usePropagateDimensions();    
    
    const updateInternalDimensionsInPx = useCallback(() => {
        const { width: currentWidth, height: currentHeight } = nodeDimensions.current;
        const width = percentToPx(
            currentWidth,
            resizable.current &&
                getElementDimensions(resizable.current.resizable.parentElement).width
        );
        const height = percentToPx(
            currentHeight,
            resizable.current &&
                getElementDimensions(resizable.current.resizable.parentElement).height
        );
        setInternalDimensions({ width, height });
    }, []);

    const updateInternalDimensionsWithOriginal = useCallback(() => {
        const { width: currentWidth, height: currentHeight } = nodeDimensions.current;
        setInternalDimensions({ width: currentWidth, height: currentHeight });
    }, []);

    const getParentWidth = () => {
        if (isRootNode) return MAX_RECIPE_WIDTH_PX;
        
        const parentDom = resizable.current && resizable.current.resizable && resizable.current.resizable.parentElement;
        if (parentDom) {
            const parentClientWidth = parentDom.clientWidth;
            
            const parentStyle = window.getComputedStyle(parentDom);

            const paddingLeft = parseInt(parentStyle.paddingLeft || 0);
            const paddingRight = parseInt(parentStyle.paddingRight || 0);
            const totalHorizontalPadding = paddingLeft + paddingRight;

            return parentClientWidth - totalHorizontalPadding;
        }
        return undefined;
    }
    
    const getUpdatedDimensions = (width, height) => {
        const dom = resizable.current.resizable;
        if (!dom) return;
        const currentWidth = parseInt(editingDimensions.current.width);
        const currentHeight = parseInt(editingDimensions.current.height);
        return {
            width: currentWidth + parseInt(width),
            height: currentHeight + parseInt(height),
        };
    };

    const savePropsDebounced = useCallback(
        debounce((width, height) => {
            setProp((prop) => {
                prop[propKey.width] = width;
                prop[propKey.height] = height;
            }, 500);
        }, 50),
        [setProp, propKey]
    );
    
    const handleRowResize = useCallback((resizedElementWidthPx) => {
        if (!parent || parentDirection !== 'row' || !propKey.width) {
            return false;
        }

        const parentNode = query.node(parent).get();
        const childrenIds = parentNode.data.nodes;
        const currentIndex = childrenIds.indexOf(id);
        
        const isRightHandle = resizeSide.current === 'right' || resizeSide.current === 'bottomRight' || resizeSide.current === 'topRight';
        const isLeftHandle = resizeSide.current === 'left' || resizeSide.current === 'bottomLeft' || resizeSide.current === 'topLeft';
        
        const MIN_PERCENT_WIDTH = 5; 
        
        let siblingIdToAdjust = null;
        let changeMade = false;

        if (isRightHandle) {
            if (currentIndex === childrenIds.length - 1) {
                return true; 
            }
            
            if (currentIndex < childrenIds.length - 1) {
                siblingIdToAdjust = childrenIds[currentIndex + 1];
            }
        } else if (isLeftHandle) {
            if (currentIndex > 0) {
                siblingIdToAdjust = childrenIds[currentIndex - 1];
            }
        }

        if (!siblingIdToAdjust) {
            changeMade = false;
        }
        
        const parentDom = resizable.current.resizable.parentElement;
        if (!parentDom) return false;
        const parentWidthPx = getElementDimensions(parentDom).width;

        let newResizedWidthPercent = pxToPercent(resizedElementWidthPx, parentWidthPx);
        
        if (siblingIdToAdjust) {
            changeMade = true;
            
            const otherChildrenTotalWidthPercent = childrenIds
                .filter(childId => childId !== id && childId !== siblingIdToAdjust) 
                .reduce((total, childId) => {
                    const childNode = query.node(childId).get();
                    if (!childNode || !childNode.data) {
                         return total;
                    }
                    
                    const childWidth = childNode.data.props[propKey.width];
                    if (isPercentage(childWidth)) {
                        return total + parseFloat(childWidth);
                    }
                    return total + 0; 
                }, 0);

            const sharedSpace = 100 - otherChildrenTotalWidthPercent;
            
            const maxAllowedWidth = sharedSpace - MIN_PERCENT_WIDTH;
            
            newResizedWidthPercent = Math.max(MIN_PERCENT_WIDTH, newResizedWidthPercent);
            newResizedWidthPercent = Math.min(maxAllowedWidth, newResizedWidthPercent);
            
            let newSiblingWidthPercent = sharedSpace - newResizedWidthPercent;
            newSiblingWidthPercent = Math.max(MIN_PERCENT_WIDTH, newSiblingWidthPercent);

            actions.setProp(id, (prop) => {
                prop[propKey.width] = `${newResizedWidthPercent}%`;
            }); 

            actions.setProp(siblingIdToAdjust, (prop) => {
                prop[propKey.width] = `${newSiblingWidthPercent}%`;
            }); 
            
            actions.setProp(parent, (prop) => prop.updatedAt = Date.now()); 
        }

        if (!changeMade) {
            newResizedWidthPercent = Math.max(MIN_PERCENT_WIDTH, newResizedWidthPercent);

            actions.setProp(id, (prop) => {
                prop[propKey.width] = `${newResizedWidthPercent}%`;
            }); 
            actions.setProp(parent, (prop) => prop.updatedAt = Date.now()); 
        }

        return true; 
    }, [id, parent, parentDirection, propKey.width, actions, query, resizable]);
    
    useEffect(() => {
        if (!isResizing.current) updateInternalDimensionsWithOriginal();
        
        if (active && inNodeContext) {
            const timeout = setTimeout(() => {
                calculateMinDimensions();
            }, 0);    
            
            return () => clearTimeout(timeout);
        }
    }, [
        nodeWidth,    
        nodeHeight,
        updatedAt, 
        updateInternalDimensionsWithOriginal,    
        active,
        inNodeContext,    
        calculateMinDimensions
    ]);

    useEffect(() => {
        const listener = debounce(updateInternalDimensionsWithOriginal, 1);
        window.addEventListener('resize', listener);
        return () => {
            window.removeEventListener('resize', listener);
        };
    }, [updateInternalDimensionsWithOriginal]);

    const enabledHandles = [
        'top', 'left', 'bottom', 'right',
        'topLeft', 'topRight', 'bottomLeft', 'bottomRight'
    ].reduce((acc, key) => {
        acc[key] = active && inNodeContext;
        return acc;
    }, {});

    if (aspectRatio) {
        enabledHandles.top = false;
        enabledHandles.bottom = false;
        enabledHandles.left = false;
        enabledHandles.right = false;
        
        enabledHandles.topLeft = true;
        enabledHandles.topRight = true;
        enabledHandles.bottomLeft = true;
        enabledHandles.bottomRight = true;
    }

    return (
        <Resizable
            enable={enabledHandles}
            minWidth={minDimensions.minWidth}    
            minHeight={minDimensions.minHeight}
            maxWidth={getParentWidth()}
            className={classes.resizable}
            ref={(ref) => {
                if (ref) {
                    resizable.current = ref;
                    connect(resizable.current.resizable);
                }
            }}
            size={internalDimensions}
            onResizeStart={(e, direction) => {
                resizeSide.current = direction; 
                calculateMinDimensions();    
                updateInternalDimensionsInPx();
                e.preventDefault();
                e.stopPropagation();
                const dom = resizable.current.resizable;
                if (!dom) return;
                editingDimensions.current = {
                    width: dom.getBoundingClientRect().width,
                    height: dom.getBoundingClientRect().height,
                };
                isResizing.current = true;
            }}
            onResize={(_, direction, ___, d) => {
                const dom = resizable.current.resizable;
                let { width: newWidthPx, height: newHeightPx } = getUpdatedDimensions(d.width, d.height);
                
                if (aspectRatio) {
                    if (direction.includes('left') || direction.includes('right')) {
                        newHeightPx = newWidthPx / aspectRatio;
                    } else if (direction.includes('top') || direction.includes('bottom')) {
                        newWidthPx = newHeightPx * aspectRatio;
                    }
                    
                    setInternalDimensions({ width: newWidthPx, height: newHeightPx });
                }
                
                if (parentDirection === 'row' && propKey.width && parent) {
                } else {
                    let width, height;
                    
                    if (isPercentage(nodeWidth))
                        width = pxToPercent(newWidthPx, getElementDimensions(dom.parentElement).width) + '%';
                    else
                        width = `${newWidthPx}px`;

                    if (isPercentage(nodeHeight))
                        height = pxToPercent(newHeightPx, getElementDimensions(dom.parentElement).height) + '%';
                    else
                        height = `${newHeightPx}px`;

                    savePropsDebounced(width, height);
                }
            }}
            onResizeStop={() => {
                isResizing.current = false;

                const dom = resizable.current.resizable;
                let finalWidthPx = dom.getBoundingClientRect().width;
                let finalHeightPx = dom.getBoundingClientRect().height;
                
                if (aspectRatio) {
                    finalHeightPx = finalWidthPx / aspectRatio;
                }

                let resizeHandled = false;

                if (parentDirection === 'row' && propKey.width && parent) {
                    resizeHandled = handleRowResize(finalWidthPx);
                } 
                
                if (!resizeHandled) {
                    let finalWidthProp = `${finalWidthPx}px`;
                    let finalHeightProp = `${finalHeightPx}px`;
                    
                    if (isPercentage(nodeWidth)) {
                        const parentWidthPx = getElementDimensions(dom.parentElement).width;
                        finalWidthProp = pxToPercent(finalWidthPx, parentWidthPx) + '%';
                    }
                    if (isPercentage(nodeHeight)) {
                         const parentHeightPx = getElementDimensions(dom.parentElement).height;
                         finalHeightProp = pxToPercent(finalHeightPx, parentHeightPx) + '%';
                    }
                    
                    setProp((prop) => {
                        prop[propKey.width] = finalWidthProp;
                        prop[propKey.height] = finalHeightProp;
                    });
                }

                updateInternalDimensionsWithOriginal();
                calculateMinDimensions();

                if (parent) {
                    propagateHeight(parent);
                    propagateWidth(parent);
                }
            }}
            {...props}
        >
            {children}
            {active && <div className={classes.indicators}>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
            </div>}
        </Resizable>
    );
};
